import { lazy, Suspense } from "react";
import { EmptyState, Pagination } from "@/shared/components";
import { useContractsListPage } from "@/features/hr/contracts/hooks";
import { ContractsCardSkeleton, ContractsFilter, ContractsHeader, ContractsPageSkeleton } from "./components";

const ContractsTableView = lazy(() => import("./components/ContractsTableView").then((m) => ({ default: m.default })));
const ContractsCardsView = lazy(() => import("./components/ContractsCardsView").then((m) => ({ default: m.default })));

export default function ContractsListPage() {
  const {
    fillDrafts,
    isLoading,
    isError,
    refetchContracts,
    search,
    setSearch,
    startDateRange,
    setStartDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    view,
    setView,
    locale,
    sortConfig,
    handleSort,
    filteredContracts,
    pageContracts,
    pagination,
    handleResetFilters,
    t,
  } = useContractsListPage();

  if (isLoading && filteredContracts.length === 0) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <ContractsHeader />
          <ContractsPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 min-w-0">
          <ContractsHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>

      <section className="flex-1 min-w-0">
        <ContractsHeader />

        <div className="radius-3xl surface-base-fill p-7">
          <ContractsFilter
            search={search}
            onSearchChange={setSearch}
            startDateRange={startDateRange}
            onStartDateRangeChange={setStartDateRange}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            statusOptions={statusOptions}
            hasActiveFilters={activeFilters}
            onReset={handleResetFilters}
            locale={locale}
            view={view}
            onViewChange={setView}
          />

          {filteredContracts.length === 0 && fillDrafts.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              {view === "table" ? (
                <Suspense fallback={<ContractsPageSkeleton variant="tableOnly" />}>
                  <ContractsTableView
                    contracts={pageContracts.map((g) => g.contract)}
                    fillDrafts={fillDrafts}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    locale={locale}
                  />
                </Suspense>
              ) : (
                <Suspense fallback={<ContractsCardSkeleton />}>
                  <ContractsCardsView contracts={pageContracts} fillDrafts={fillDrafts} locale={locale} refetchContracts={refetchContracts} />
                </Suspense>
              )}

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={filteredContracts.length}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}
